import DateAndTime from 'date-and-time';
import TimeZonePlugin from 'date-and-time/plugin/timezone';

import {
  fnConvertTimeZoneAbbrToIana,
  oCurrencyFormatter,
  truthyDadValues,
} from 'utils/common';

DateAndTime.plugin(TimeZonePlugin);

/**
 * Formats an event's start and end dates.
 *
 * @param {string} sStartDate - Start date time from DAD
 * @param {string} sEndDate - End date time from DAD
 * @returns {string}
 */
export const fnFormatEventDate = (sStartDate, sEndDate) => {
  if (!sStartDate && !sEndDate) {
    return '';
  }

  let sFormatted = '';
  const oStartDate = DateAndTime.parse(sStartDate, 'YYYY-MM-DDTHH:mm:ss');
  const oEndDate = DateAndTime.parse(sEndDate, 'YYYY-MM-DDTHH:mm:ss');

  const sStartMonth = DateAndTime.format(oStartDate, 'MMM');
  const sStartDay = DateAndTime.format(oStartDate, 'DD');

  const sEndMonth = DateAndTime.format(oEndDate, 'MMM');
  const sEndDay = DateAndTime.format(oEndDate, 'DD');

  const bIsSingleDayEvent = DateAndTime.isSameDay(oStartDate, oEndDate);

  if (bIsSingleDayEvent) {
    sFormatted = `${sStartMonth} ${sStartDay}`; // Jan 01
  } else if (sStartMonth === sEndMonth) {
    sFormatted = `${sStartMonth} ${sStartDay} - ${sEndDay}`; // Jan 01 - 11
  } else {
    sFormatted = `${sStartMonth} ${sStartDay} - ${sEndMonth} ${sEndDay}`; // Jan 25 - Feb 02
  }
  return sFormatted;
};

/**
 * Formats an event's start and end times.
 *
 * @param {string} sStartDate - Start date time from DAD
 * @param {string} sEndDate - End date time from DAD
 * @param {string} sTimezone - Timezone abbreviation from DAD
 * @returns {string}
 */
export const fnFormatEventTime = (sStartDate, sEndDate, sTimezone = 'ET') => {
  if (!sStartDate && !sEndDate) {
    return '';
  }

  const oStartDate = DateAndTime.parse(sStartDate, 'YYYY-MM-DDTHH:mm:ss');
  const oEndDate = DateAndTime.parse(sEndDate, 'YYYY-MM-DDTHH:mm:ss');

  const sStartTime = DateAndTime.format(oStartDate, 'hh:mm');
  const sEndTime = DateAndTime.format(oEndDate, 'hh:mm');

  const bTimeIsTbd = sStartTime === '12:00' && sEndTime === '12:01';
  const bIsSingleDayEvent = DateAndTime.isSameDay(oStartDate, oEndDate);
  const sTimeFormat = bIsSingleDayEvent
    ? 'h:mm A' // 10:00 AM
    : 'MMM DD at h:mm A'; // Jan 27 at 10:00 AM
  const sFormatted = bTimeIsTbd
    ? 'To Be Announced'
    : `${DateAndTime.format(oStartDate, sTimeFormat)} - ${DateAndTime.format(
        oEndDate,
        sTimeFormat
      )} ${sTimezone}`;

  return sFormatted;
};

/**
 * Applies USD currency formatting and handles cost ranges.
 *
 * @param {object} oEvent - Event object from DAD
 * @returns {string}
 */
export const fnFormatEventCost = (oEvent) => {
  let sCost = '';

  const bHasCost = oEvent.REGISTRATION.HAS_COST === 1;

  if (bHasCost) {
    const { MIN, MAX } = oEvent.REGISTRATION.COST_RANGE;
    if (MIN === MAX) {
      sCost = oCurrencyFormatter.format(MIN);
    } else {
      sCost = `${oCurrencyFormatter.format(MIN)} - ${oCurrencyFormatter.format(
        MAX
      )}`;
    }
  }

  return sCost;
};

export const fnIsEventRegistrationOpen = (
  sOpenDateTime,
  sCloseDateTime,
  sTimeZone
) => {
  if (!sOpenDateTime || !sCloseDateTime) {
    return false;
  }
  const oNow = new Date();
  const sEventIanaTimeZone = fnConvertTimeZoneAbbrToIana(sTimeZone);
  let oOpenDateTime;
  let oCloseDateTime;
  if (sEventIanaTimeZone) {
    oOpenDateTime = DateAndTime.parseTZ(
      sOpenDateTime,
      'YYYY-MM-DDTHH:mm:ss',
      sEventIanaTimeZone
    );
    oCloseDateTime = DateAndTime.parseTZ(
      sCloseDateTime,
      'YYYY-MM-DDTHH:mm:ss',
      sEventIanaTimeZone
    );
  } else {
    oOpenDateTime = DateAndTime.parse(sOpenDateTime, 'YYYY-MM-DDTHH:mm:ss');
    oCloseDateTime = DateAndTime.parse(sCloseDateTime, 'YYYY-MM-DDTHH:mm:ss');
  }
  return oOpenDateTime <= oNow && oCloseDateTime >= oNow;
};

export const fnIsEventAtCapacity = (oEvent) => {
  const { TOTAL_CAPACITY, TOTAL_REGISTRATIONS } = oEvent.REGISTRATION;
  const bHasSetCapacity = TOTAL_CAPACITY > 0;
  const iSpotsRemaining = TOTAL_CAPACITY - TOTAL_REGISTRATIONS;
  return bHasSetCapacity && iSpotsRemaining <= 0;
};

/**
 * Determines if an event is available for registration.
 *
 * An event is registrable when:
 * - The event is not cancelled
 * - The user is not already registered
 * - The registration window is currently open
 * - The event is not at capacity
 * - The user has not declined the invitation
 *
 * @param {object} oEvent - The event object to check
 * @returns {boolean} True if the user can register for the event, false otherwise
 */
export const fnIsEventRegistrable = (oEvent) => {
  if (!oEvent || !oEvent.REGISTRATION) {
    console.warn(
      'fnIsEventRegistrable: Missing required parameter `oEvent`',
      oEvent
    );
    return false;
  }

  const { IS_CANCELLED, TIME_ZONE } = oEvent;
  const { IS_REGISTERED, OPEN_DATETIME, CLOSE_DATETIME } = oEvent.REGISTRATION;

  const bIsEventCancelled = truthyDadValues.includes(IS_CANCELLED);
  if (bIsEventCancelled) {
    return false;
  }

  const bIsRegistered = truthyDadValues.includes(IS_REGISTERED);
  if (bIsRegistered) {
    return false; // User is already registered
  }

  const bIsRegistrationOpen = fnIsEventRegistrationOpen(
    OPEN_DATETIME,
    CLOSE_DATETIME,
    TIME_ZONE
  );
  if (!bIsRegistrationOpen) {
    return false; // Registration window is not open
  }

  const bAtCapacity = fnIsEventAtCapacity(oEvent);
  if (bAtCapacity) {
    return false; // Event is at capacity
  }

  const sRsvpStatus = oEvent.INVITATION?.RSVP_STATUS;
  if (sRsvpStatus === 'Declined') {
    return false; // User already declined the invitation
  }

  return true;
};

/**
 * Determines if an event's waitlist is available to join.
 *
 * An event is waitlistable when:
 * - The event is at capacity
 * - The event allows waitlisting
 * - The user is not already on the waitlist
 *
 * @param {object} oEvent - The event object to check
 * @returns {boolean} True if the user can join the waitlist, false otherwise
 */
export const fnIsEventWaitlistable = (oEvent) => {
  if (!oEvent || !oEvent.REGISTRATION) {
    console.warn(
      'fnIsEventWaitlistable: Missing required parameter `oEvent`',
      oEvent
    );
    return false;
  }

  if (!fnIsEventAtCapacity(oEvent)) {
    return false; // Waitlist is only available when event is at capacity
  }

  const { ALLOW_WAITLIST, IS_WAITLISTED } = oEvent.REGISTRATION;

  if (!truthyDadValues.includes(ALLOW_WAITLIST)) {
    return false; // Event does not allow waitlisting
  }

  if (truthyDadValues.includes(IS_WAITLISTED)) {
    return false; // User is already on the waitlist
  }

  return true;
};

/**
 * Determines if RSVP buttons should be displayed for an event.
 *
 * RSVP buttons are shown when:
 * - The user is invited and has not yet responded (RSVP status is "No Reply")
 * - The user is unauthenticated and the event has invitees
 * - The registration window is currently open
 *
 * RSVP buttons are NOT shown when:
 * - The invitation status is "Declined"
 * - The registration window is closed
 *
 * @param {object} oEvent - The event object to check
 * @param {boolean} bIsUserAuthenticated - Whether the current user is authenticated
 * @returns {boolean} True if RSVP buttons should be displayed, false otherwise
 */
export const fnIsEventRsvpable = (oEvent, bIsUserAuthenticated) => {
  if (!oEvent || !oEvent.INVITATION) {
    console.warn(
      'fnIsEventRsvpable: Missing required parameter `oEvent`',
      oEvent
    );
    return false;
  }

  const { IS_INVITED, RSVP_STATUS, HAS_INVITEES } = oEvent.INVITATION;

  if (RSVP_STATUS === 'Declined') {
    return false; // User already declined the invitation
  }

  // Check if registration window is open
  if (oEvent.REGISTRATION) {
    const { OPEN_DATETIME, CLOSE_DATETIME } = oEvent.REGISTRATION;
    const { TIME_ZONE } = oEvent;
    const bIsRegistrationOpen = fnIsEventRegistrationOpen(
      OPEN_DATETIME,
      CLOSE_DATETIME,
      TIME_ZONE
    );
    if (!bIsRegistrationOpen) {
      return false; // Registration window is not open
    }
  }

  if (truthyDadValues.includes(IS_INVITED) && RSVP_STATUS === 'No Reply') {
    return true; // User is invited and has not responded yet
  }

  if (!bIsUserAuthenticated && truthyDadValues.includes(HAS_INVITEES)) {
    return true; // Unauthenticated user, but the event has invitees
  }

  return false;
};
